<template>
  <div class="curation-search-map">
    <div class="map-control" v-if="editstate">
      <button class="jps-tag jps-sub-orange" @click="setCenter()">
        {{
          lss(
            '地図の初期中心位置とズームをセット',
            'Set Initial location and zoom',
          )
        }}
      </button>
      <BaseSelect class="jps-round-select" v-model="mode" :enum="mapModeDef" />
    </div>
    <div class="map-result">
      <div
        ref="list"
        class="map-result-list"
        v-if="editstate && !setCoordinateItem"
      >
        <ul>
          <li :id="'id_' + item.key" v-for="item in items" :key="item.key">
            <article v-if="!item.readNext" class="media">
              <figure
                class="media-left jps-relative"
                @click="emits('show-modal', item)"
              >
                <p class="image">
                  <UiImageSelection
                    :w="80"
                    :h="80"
                    :type="item.type"
                    :image="item.image"
                  />
                  <ListEditIcon
                    :item="item"
                    field="image"
                    :editstate="editstate"
                  />
                </p>
              </figure>
              <div class="media-content">
                <span class="jps-map-bullet-title has-text-weight-bold">
                  <BaseLink
                    v-bind="baseLinkProps(item)"
                    @click="emits('show-modal', item)"
                  >
                    {{ ls(item.title) }}
                  </BaseLink>
                  <ListEditIcon
                    :item="item"
                    field="title"
                    :editstate="editstate"
                  />
                  <ListEditIcon
                    :item="item"
                    field="url"
                    :editstate="editstate"
                  />
                  <ListEditIcon
                    :item="item"
                    field="linkUrl"
                    :editstate="editstate"
                  />
                  <UiButtonIcon
                    tag="button"
                    v-if="editable && item.def"
                    class="jps-icon-button list-delete"
                    @click.stop="emits('delete-item', item)"
                    icon="mdi/close-circle"
                  />
                </span>

                <p
                  class="jps-bullet-basic is-size-7"
                  v-if="!isEmpty(item.contributor)"
                >
                  <span>{{ ljoin(item.contributor) }}</span>
                </p>
                <p class="jps-bullet-provider is-size-7">
                  <template v-if="item.type === 'item' && item.data">
                    <span v-if="isJps && item.data.common.provider"
                      >{{ item.data.common.provider }}/</span
                    >
                    <UiDbSpan :id="item.data.common.database" />
                  </template>
                  <template v-else-if="isJps">
                    <UiOrgSpan
                      v-if="item.ownerOrgId"
                      :id="item.ownerOrgId"
                    />
                  </template>
                </p>
                <p class="jps-bullet-kv is-size-7">
                  {{ ls(item.description) }}
                  <ListEditIcon
                    :item="item"
                    field="description"
                    :editstate="editstate"
                  />
                </p>
                <button
                  v-if="isCoordinateSettable(item.def.type)"
                  class="jps-icon-button set-coord"
                  @click="setCoordinate(item)"
                >
                  {{ lss('座標を設定', 'Set coordinates') }}
                </button>
                <p class="jps-message jps-red" v-if="!item.latlon">
                  {{ lss('座標の無いデータです。', 'No coordinates') }}
                </p>
              </div>
            </article>
            <SearchResultDiv v-else :item="item" :editstate="editstate" />
          </li>
          <li
            v-if="editable && editstate.mode !== 'note'"
            class="add-button-li"
          >
            <button
              class="jps-icon-button list-add-button jps-sub-orange"
              @click="emits('add')"
            >
              <BaseIcon name="mdi/plus-circle" :alt="ls(addPartMsg)" size="size-4" />
            </button>
          </li>
        </ul>
      </div>
      <div class="set-coordinate-msg" v-if="setCoordinateItem">
        {{
          lss(
            '地図上をクリックして座標を指定して下さい',
            'Click map to set coordinates',
          )
        }}
        <div class="inputs">
          <input
            class="jps-input"
            v-model="setCoordinateItem.latlon.lat"
            :placeholder="lss('緯度', 'Latitude')"
          />
          <input
            class="jps-input"
            v-model="setCoordinateItem.latlon.lon"
            :placeholder="lss('経度', 'Longtiude')"
          />
          <button class="jps-button" @click="confirmCoord()">OK</button>
        </div>
      </div>
      <ClientOnly>
        <UiMap
          ref="uiMap"
          :points="points"
          @click-point="clickPoint($event)"
          @click-map="clickMap"
          v-bind="def.mapParam"
        />
      </ClientOnly>
    </div>
    <span class="is-size-7" v-if="editstate && center"
      >表示：緯度{{ round(center.lat, 0.01) }}/経度{{
        round(center.lon, 0.01)
      }}/ズーム{{ zoom }}</span
    >
  </div>
</template>

<script lang="ts" setup>
import type { UiMap } from '#components'

const { ls, lss, ljoin, langPath } = useI18n()
const { isJps } = useServiceType()
const { snackbarMsg  } = useSnackbar()
const router = useRouter()

interface Props {
  editstate?: CurationEditState
  def : PartList
  items: ListElement[]
  viewParam?: CurationListViewParam
}
const props = withDefaults(defineProps<Props>(), {})

const emits = defineEmits(['delete-item', 'show-modal', 'add'])

const {
  baseLinkProps,
  editable,
  addPartMsg,
} = useCurationListView(props.editstate, props.def)

const points = computed(() => {
  const isDisplayImage: boolean = props.viewParam?.map?.isDisplayImage ?? false
  const getImage = (item: any) => {
    if (isDisplayImage && item.type === 'item') {
      return item.image?.url
    } else {
      return item.image?.thumbnailUrl
    }
  }
  return props.items
    .filter((item) => item.latlon)
    .map((item) => {
      return {
        lat: item.latlon.lat,
        lon: item.latlon.lon,
        image: getImage(item),
        label: ls(item.title),
        id: item.key,
      }
    })
})

const list = computed(() => {
  return props.items.filter((item) => item.latlon)
})

let mapModeDef = ref(MAP_MODE_DEF)

const mode = computed({
  get: () => {
    if (props.def.mapParam) return props.def.mapParam.mode
    return 'NORMAL'
  },
  set: (v) => {
    if (!props.def.mapParam) {
      props.def.mapParam = {
        center: null,
        zoom: null,
        mode: v,
      }
    } else {
      props.def.mapParam.mode = v
    }
  }
})

const zoom = computed(() => {
  if (props.def.mapParam) return props.def.mapParam.zoom
  return null
})

const center = computed(() => {
  if (props.def.mapParam) return props.def.mapParam.center
  return null
})

const uiMap = ref<InstanceType<typeof UiMap>>()
const setCenter = () => {
  if (!props.def.mapParam) {
    props.def.mapParam = {
      center: null,
      zoom: null,
      mode: 'NORMAL',
    }
  }
  if (!uiMap.value) return
  let p = uiMap.value.getView()
  props.def.mapParam.center = p.latlon
  props.def.mapParam.zoom = p.zoom
  snackbarMsg(
    {
      ja: '初期中心位置とズームを、今の場所にセットしました',
      en: 'Set center',
    }
  )
}

const isCoordinateSettable = (type: string) => {
  return [
    'jps-curation-list-curation',
    'jps-curation-list-external-link',
    'jps-curation-list-organization',
    'jps-curation-list-item',
  ].includes(type)
}

const setCoordinateItem:Ref<any> = ref(null)
const setCoordinate = (i: ListElement) => {
  if (!i.def.latlon) {
    i.def.latlon = {
      lat: 35.3622222,
      lon: 138.7313889,
    }
  }
  setCoordinateItem.value = i.def
}

const confirmCoord = () => {
  setCoordinateItem.value = null
}

const clickMap =  (latlng: any) => {
  if (setCoordinateItem.value) {
    setCoordinateItem.value.latlon = {
      lat: latlng.lat,
      lon: latlng.lng,
    }
    setCoordinateItem.value = null
  }
}

const clickPoint =  (ev: { id: string }) => {
  let item = props.items.filter((i) => i.key === ev.id).pop()
  if (item) {
    if (props.def.openActionType === 'link') {
      if (item.internalLink) {
        router.push(langPath(item.internalLink as string))
      } else if (item.link) {
        if (item.link.startsWith('/')) {
          router.push(langPath(item.link))
        } else {
          location.href = item.link
        }
      }
    } else {
      emits('show-modal', item)
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.curation-search-map {
  .map-control {
    display: flex;
    margin-bottom: 0.5rem;
  }
  .map-result {
    height: 400px;
    .jps-map {
      height: 400px;
    }

    .set-coord {
      color: $jps-key-orange !important;
    }

    .set-coordinate-msg {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      z-index: 1000;
      background-color: rgba(255, 255, 255, 0.5);
      align-items: center;
      padding-left: 4rem;
      .inputs {
        margin-left: auto;
        display: flex;
        .jps-input {
          width: 8rem;
        }
        .jps-button {
          border-radius: 0;
        }
      }
    }
    .show-list-button {
      position: absolute;
      top: 0;
      right: 0;
      z-index: 1000;
    }
    position: relative;

    .map-result-list {
      position: absolute;
      width: 24rem;
      padding: 1rem;
      /* height: 367px; */
      /* top: 33px; */
      height: 100%;
      right: 0;
      overflow-y: scroll;
      z-index: 950;
      background: rgba(256, 256, 256, 0.8);
      ul li {
        margin: 0.5rem 0 0.5rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid $jps-gray-300;
        &:last-child {
          border-bottom: none;
        }
      }
      .add-button-li {
        text-align: center;
        padding: 0.5rem;
      }
      .jps-map-bullet-title {
        display: flex;
        .list-delete {
          margin-left: auto;
        }
      }
      .jps-message {
        font-size: 0.75rem;
        margin-bottom: 0.25rem;
      }
    }
  }

  // 画像編集アイコンの位置調整
  :deep(.edit-image-static) {
    position: absolute;
    right: 0px;
    bottom: -6px;
  }
}

</style>

<style lang="scss">
.curation-search-map {
  .leaflet-marker-pane {
    img {
      border-radius: 6px;
    }
  }
}
</style>
